import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, Plus, Sparkles, Search, MoreVertical, FileText, X, Zap, Check, Users } from 'lucide-react';
import { AppShell } from '../components/AppShell';
type AlertType = 'keyword' | 'semantic';
function CreateAlertModal({
  onClose
}: {
  onClose: () => void;
}) {
  const [alertType, setAlertType] = useState<AlertType>('keyword');
  const [keyword, setKeyword] = useState('');
  const [prompt, setPrompt] = useState('');
  const [sources, setSources] = useState<string[]>([]);
  const [step, setStep] = useState<'type' | 'config' | 'compartir' | 'confirm'>('type');
  const [emailInput, setEmailInput] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const availableSources = ['ANMAT', 'Boletín Oficial', 'SENASA', 'Ministerio de Salud', 'PAMI', 'INAME'];
  const availableGroups = [{
    id: 'g1',
    name: 'Asuntos Regulatorios',
    members: 3
  }, {
    id: 'g2',
    name: 'Dirección Técnica',
    members: 2
  }, {
    id: 'g3',
    name: 'Comercio Exterior',
    members: 4
  }];
  const toggleSource = (source: string) => {
    setSources(prev => prev.includes(source) ? prev.filter(s => s !== source) : [...prev, source]);
  };
  const handleAddUser = () => {
    if (emailInput.trim() && !selectedUsers.includes(emailInput.trim())) {
      setSelectedUsers([...selectedUsers, emailInput.trim()]);
      setEmailInput('');
    }
  };
  const toggleGroup = (groupId: string) => {
    setSelectedGroups(prev => prev.includes(groupId) ? prev.filter(id => id !== groupId) : [...prev, groupId]);
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-379">
      <motion.div className="absolute inset-0 bg-navy-950/40 backdrop-blur-sm" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={onClose} data-id="element-380" />
      <motion.div className="relative bg-white rounded-xl shadow-dropdown border border-slate-200 w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]" initial={{
      opacity: 0,
      scale: 0.95,
      y: 10
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 10
    }} transition={{
      duration: 0.2
    }} data-id="element-381">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-200 shrink-0" data-id="element-382">
          <h2 className="text-lg text-navy-950" data-id="element-383">Crear Nueva Alerta</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-navy-950 transition-colors p-1" data-id="element-384">
            <X size={18} data-id="element-385" />
          </button>
        </div>

        <div className="overflow-y-auto overflow-x-hidden" data-id="element-386">
          <AnimatePresence mode="wait" data-id="element-387">
            {/* Step 1: Choose Type */}
            {step === 'type' && <motion.div key="type" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="p-5 space-y-4" data-id="element-388">
                <p className="text-sm text-navy-600" data-id="element-389">
                  ¿Qué tipo de alerta desea crear?
                </p>

                <button onClick={() => {
              setAlertType('keyword');
              setStep('config');
            }} className="w-full text-left p-4 rounded-lg border border-slate-200 hover:border-brand-300 hover:bg-brand-50/30 transition-all group" data-id="element-390">
                  <div className="flex items-start gap-3" data-id="element-391">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 group-hover:bg-brand-100 flex items-center justify-center shrink-0 transition-colors" data-id="element-392">
                      <Search size={18} className="text-navy-600 group-hover:text-brand-600" data-id="element-393" />
                    </div>
                    <div data-id="element-394">
                      <h3 className="text-sm font-medium text-navy-950 mb-1" data-id="element-395">
                        Palabra Clave
                      </h3>
                      <p className="text-xs text-navy-600" data-id="element-396">
                        Monitorea documentos que contengan una palabra o frase
                        exacta.
                      </p>
                    </div>
                  </div>
                </button>

                <button onClick={() => {
              setAlertType('semantic');
              setStep('config');
            }} className="w-full text-left p-4 rounded-lg border border-brand-200 hover:border-brand-400 bg-brand-50/20 hover:bg-brand-50/50 transition-all group relative overflow-hidden" data-id="element-397">
                  <div className="absolute top-2 right-2" data-id="element-398">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-brand-600 bg-brand-100 px-2 py-0.5 rounded-full" data-id="element-399">
                      Premium
                    </span>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-400">
                    <div className="w-10 h-10 rounded-lg bg-brand-100 flex items-center justify-center shrink-0" data-id="element-401">
                      <Sparkles size={18} className="text-brand-600" data-id="element-402" />
                    </div>
                    <div data-id="element-403">
                      <h3 className="text-sm font-medium text-navy-950 mb-1" data-id="element-404">
                        Búsqueda Semántica
                      </h3>
                      <p className="text-xs text-navy-600" data-id="element-405">
                        Describa en lenguaje natural lo que desea monitorear. La
                        IA entiende el contexto, no solo las palabras.
                      </p>
                    </div>
                  </div>
                </button>
              </motion.div>}

            {/* Step 2: Configure */}
            {step === 'config' && <motion.div key="config" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="p-5 space-y-5" data-id="element-406">
                <div className="flex items-center gap-2 text-xs font-medium px-2 py-1 rounded-md w-fit border" style={{
              color: alertType === 'semantic' ? '#2563EB' : '#475569',
              backgroundColor: alertType === 'semantic' ? '#EFF6FF' : '#F1F5F9',
              borderColor: alertType === 'semantic' ? '#BFDBFE' : '#E2E8F0'
            }} data-id="element-407">
                  {alertType === 'semantic' ? <Sparkles size={12} data-id="element-408" /> : <Search size={12} data-id="element-409" />}
                  {alertType === 'semantic' ? 'Alerta Semántica' : 'Palabra Clave'}
                </div>

                {alertType === 'keyword' ? <div className="space-y-1.5" data-id="element-410">
                    <label className="block text-sm font-medium text-navy-950" data-id="element-411">
                      Palabra clave o frase
                    </label>
                    <input type="text" value={keyword} onChange={e => setKeyword(e.target.value)} placeholder='Ej. "Vademécum PAMI", "trazabilidad medicamentos"' className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500" autoFocus data-id="element-412" />
                  </div> : <div className="space-y-1.5" data-id="element-413">
                    <label className="block text-sm font-medium text-navy-950" data-id="element-414">
                      Describa lo que desea monitorear
                    </label>
                    <textarea value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="Ej. Notificarme sobre cualquier cambio normativo relacionado con la importación de principios activos oncológicos, incluyendo restricciones, nuevos requisitos de documentación o cambios en los procesos de habilitación..." className="w-full h-28 px-3 py-2 rounded-md border border-slate-200 text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 resize-none" autoFocus data-id="element-415" />
                    <p className="text-[11px] text-slate-400" data-id="element-416">
                      La IA analizará el contexto semántico, no solo las
                      palabras exactas.
                    </p>
                  </div>}

                <div className="space-y-2" data-id="element-417">
                  <label className="block text-sm font-medium text-navy-950" data-id="element-418">
                    Fuentes a monitorear
                  </label>
                  <div className="flex flex-wrap gap-2" data-id="element-419">
                    {availableSources.map(source => <button key={source} onClick={() => toggleSource(source)} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all border ${sources.includes(source) ? 'bg-brand-500 text-white border-brand-500' : 'bg-white text-navy-600 border-slate-200 hover:border-brand-300'}`} data-id="element-420">
                        {source}
                      </button>)}
                  </div>
                </div>

                <div className="space-y-1.5" data-id="element-421">
                  <label className="block text-sm font-medium text-navy-950" data-id="element-422">
                    Nombre de la alerta{' '}
                    <span className="text-slate-400 font-normal" data-id="element-423">
                      (opcional)
                    </span>
                  </label>
                  <input type="text" placeholder="Ej. Monitoreo ANMAT oncológicos" className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500" data-id="element-424" />
                </div>
              </motion.div>}

            {/* Step 3: Compartir */}
            {step === 'compartir' && <motion.div key="compartir" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="p-5 space-y-6" data-id="element-425">
                <div data-id="element-426">
                  <h3 className="text-sm font-medium text-navy-950 mb-1" data-id="element-427">
                    Compartir alerta
                  </h3>
                  <p className="text-xs text-navy-600" data-id="element-428">
                    Invite colaboradores o grupos para que también reciban las
                    notificaciones de esta alerta.
                  </p>
                </div>

                {/* Individual Users */}
                <div className="space-y-3" data-id="element-429">
                  <label className="block text-sm font-medium text-navy-950" data-id="element-430">
                    Colaboradores individuales
                  </label>
                  <div className="flex gap-2" data-id="element-431">
                    <input type="email" value={emailInput} onChange={e => setEmailInput(e.target.value)} placeholder="email@ejemplo.com" className="flex-1 h-9 px-3 rounded-md border border-slate-200 text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500" onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddUser();
                  }
                }} data-id="element-432" />
                    <button onClick={handleAddUser} disabled={!emailInput.trim()} className="px-3 h-9 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 text-navy-600 rounded-md text-sm font-medium transition-colors" data-id="element-433">
                      Agregar
                    </button>
                  </div>
                  {selectedUsers.length > 0 && <div className="flex flex-wrap gap-2 mt-2" data-id="element-434">
                      {selectedUsers.map(user => <span key={user} className="inline-flex items-center gap-1.5 pl-1.5 pr-2 py-1 rounded-full bg-brand-50 text-brand-700 text-xs font-medium border border-brand-200" data-id="element-435">
                          <div className="w-5 h-5 rounded-full bg-brand-200 flex items-center justify-center text-[9px] font-bold text-brand-800" data-id="element-436">
                            {user.charAt(0).toUpperCase()}
                          </div>
                          {user}
                          <button onClick={() => setSelectedUsers(prev => prev.filter(u => u !== user))} className="ml-0.5 hover:text-brand-900 text-brand-500 transition-colors" data-id="element-437">
                            <X size={12} data-id="element-438" />
                          </button>
                        </span>)}
                    </div>}
                </div>

                {/* Groups */}
                <div className="space-y-3" data-id="element-439">
                  <label className="block text-sm font-medium text-navy-950" data-id="element-440">
                    Grupos
                  </label>
                  <div className="grid grid-cols-1 gap-2" data-id="element-441">
                    {availableGroups.map(group => <button key={group.id} onClick={() => toggleGroup(group.id)} className={`flex items-center justify-between p-3 rounded-lg border text-left transition-all ${selectedGroups.includes(group.id) ? 'border-brand-500 bg-brand-50/30' : 'border-slate-200 hover:border-brand-300'}`} data-id="element-442">
                        <div className="flex items-center gap-3" data-id="element-443">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${selectedGroups.includes(group.id) ? 'bg-brand-100 text-brand-600' : 'bg-slate-100 text-slate-500'}`} data-id="element-444">
                            <Users size={14} data-id="element-445" />
                          </div>
                          <div data-id="element-446">
                            <p className="text-sm font-medium text-navy-950" data-id="element-447">
                              {group.name}
                            </p>
                            <p className="text-xs text-navy-600" data-id="element-448">
                              {group.members} miembros
                            </p>
                          </div>
                        </div>
                        <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${selectedGroups.includes(group.id) ? 'bg-brand-500 border-brand-500' : 'border-slate-300'}`} data-id="element-449">
                          {selectedGroups.includes(group.id) && <Check size={12} className="text-white" data-id="element-450" />}
                        </div>
                      </button>)}
                  </div>
                </div>
              </motion.div>}

            {/* Step 4: Confirm */}
            {step === 'confirm' && <motion.div key="confirm" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="p-5 space-y-5" data-id="element-451">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3" data-id="element-452">
                  <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center shrink-0 mt-0.5" data-id="element-453">
                    <Zap size={14} className="text-white" data-id="element-454" />
                  </div>
                  <div data-id="element-455">
                    <h3 className="text-sm font-medium text-emerald-800 mb-1" data-id="element-456">
                      ¡Alerta creada exitosamente!
                    </h3>
                    <p className="text-xs text-emerald-700" data-id="element-457">
                      Su alerta está activa y comenzará a monitorear las fuentes
                      seleccionadas. Recibirá notificaciones cuando se
                      encuentren coincidencias.
                    </p>
                  </div>
                </div>

                <div className="bg-slate-50 rounded-lg p-4 space-y-3" data-id="element-458">
                  <div className="flex items-center justify-between" data-id="element-459">
                    <span className="text-xs text-slate-400" data-id="element-460">Tipo</span>
                    <span className="text-sm font-medium text-navy-950 flex items-center gap-1" data-id="element-461">
                      {alertType === 'semantic' ? <>
                          <Sparkles size={12} className="text-brand-500" data-id="element-462" />{' '}
                          Semántica
                        </> : <>
                          <Search size={12} data-id="element-463" /> Palabra Clave
                        </>}
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-464">
                    <span className="text-xs text-slate-400" data-id="element-465">Búsqueda</span>
                    <span className="text-sm font-medium text-navy-950 max-w-[250px] truncate" data-id="element-466">
                      {alertType === 'keyword' ? `"${keyword}"` : prompt.substring(0, 60) + '...'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-467">
                    <span className="text-xs text-slate-400" data-id="element-468">Fuentes</span>
                    <span className="text-sm font-medium text-navy-950" data-id="element-469">
                      {sources.length > 0 ? sources.join(', ') : 'Todas'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-slate-200" data-id="element-470">
                    <span className="text-xs text-slate-400" data-id="element-471">
                      Compartido con
                    </span>
                    <span className="text-sm font-medium text-navy-950" data-id="element-472">
                      {selectedUsers.length === 0 && selectedGroups.length === 0 ? 'Solo yo' : `${selectedUsers.length} usuarios, ${selectedGroups.length} grupos`}
                    </span>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between shrink-0" data-id="element-473">
          <button onClick={() => {
          if (step === 'config') setStep('type');else if (step === 'compartir') setStep('config');else if (step === 'confirm') onClose();else onClose();
        }} className="text-sm font-medium text-navy-600 hover:text-navy-950 px-4 py-2 rounded-md hover:bg-slate-200 transition-colors" data-id="element-474">
            {step === 'confirm' ? 'Cerrar' : step === 'type' ? 'Cancelar' : 'Atrás'}
          </button>
          {step !== 'confirm' && <button onClick={() => {
          if (step === 'type') return;
          if (step === 'config') setStep('compartir');else if (step === 'compartir') setStep('confirm');
        }} disabled={step === 'config' && alertType === 'keyword' && !keyword.trim() || step === 'config' && alertType === 'semantic' && !prompt.trim()} className="bg-brand-500 hover:bg-brand-400 disabled:bg-slate-200 disabled:text-slate-400 text-white text-sm font-medium px-5 py-2 rounded-md transition-colors shadow-sm" data-id="element-475">
              {step === 'config' ? 'Siguiente' : step === 'compartir' ? selectedUsers.length > 0 || selectedGroups.length > 0 ? 'Crear Alerta' : 'Crear sin compartir' : 'Crear Alerta'}
            </button>}
        </div>
      </motion.div>
    </div>;
}
export function AlertasPage() {
  const [activeTab, setActiveTab] = useState<'activas' | 'historial'>('activas');
  const [showCreateModal, setShowCreateModal] = useState(false);
  return <AppShell data-id="element-476">
      <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6" data-id="element-477">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-478">
          <div data-id="element-479">
            <h1 className="text-2xl text-navy-950 mb-1" data-id="element-480">Mis Alertas</h1>
            <p className="text-sm text-navy-600" data-id="element-481">
              Gestione sus monitoreos automáticos y revise el historial de
              notificaciones.
            </p>
          </div>
          <button onClick={() => setShowCreateModal(true)} className="flex items-center justify-center gap-2 bg-brand-500 hover:bg-brand-400 text-white text-sm font-medium px-5 py-2.5 rounded-md transition-colors shadow-sm whitespace-nowrap" data-id="element-482">
            <Plus size={16} data-id="element-483" />
            Crear Alerta
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-6 border-b border-slate-200" data-id="element-484">
          <button onClick={() => setActiveTab('activas')} className={`pb-3 text-sm font-medium transition-colors relative ${activeTab === 'activas' ? 'text-brand-600' : 'text-slate-400 hover:text-navy-950'}`} data-id="element-485">
            Alertas Activas (4)
            {activeTab === 'activas' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-brand-500 rounded-t-full" data-id="element-486" />}
          </button>
          <button onClick={() => setActiveTab('historial')} className={`pb-3 text-sm font-medium transition-colors relative ${activeTab === 'historial' ? 'text-brand-600' : 'text-slate-400 hover:text-navy-950'}`} data-id="element-487">
            Historial de Matches
            {activeTab === 'historial' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-brand-500 rounded-t-full" data-id="element-488" />}
          </button>
        </div>

        {activeTab === 'activas' ? <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-489">
            {/* Premium Semantic Alert */}
            <div className="bg-white rounded-xl border border-brand-200 p-5 shadow-card relative overflow-hidden group" data-id="element-490">
              <div className="absolute top-0 right-0 w-24 h-24 bg-brand-50 rounded-bl-full -z-10 group-hover:scale-110 transition-transform" data-id="element-491" />
              <div className="flex items-start justify-between mb-3" data-id="element-492">
                <div className="flex items-center gap-2 text-xs font-medium text-brand-600 bg-brand-50 px-2 py-1 rounded-md border border-brand-100" data-id="element-493">
                  <Sparkles size={12} data-id="element-494" />
                  Alerta Semántica Premium
                </div>
                <button className="text-slate-400 hover:text-navy-950" data-id="element-495">
                  <MoreVertical size={16} data-id="element-496" />
                </button>
              </div>
              <h3 className="text-base font-medium text-navy-950 mb-1" data-id="element-497">
                Cambios en trazabilidad
              </h3>
              <p className="text-sm text-navy-600 mb-4 line-clamp-2" data-id="element-498">
                Notificarme sobre cualquier cambio normativo o disposición
                relacionada con la trazabilidad de medicamentos oncológicos,
                incluso si no usan la palabra exacta.
              </p>
              <div className="flex items-center justify-between pt-4 border-t border-slate-100" data-id="element-499">
                <div className="flex flex-col" data-id="element-500">
                  <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold" data-id="element-501">
                    Matches Totales
                  </span>
                  <span className="text-sm font-medium text-navy-950" data-id="element-502">
                    12 documentos
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-503">
                  <span className="flex h-2 w-2 relative" data-id="element-504">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" data-id="element-505"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" data-id="element-506"></span>
                  </span>
                  <span className="text-xs font-medium text-emerald-600" data-id="element-507">
                    Activa
                  </span>
                </div>
              </div>
            </div>

            {/* Standard Keyword Alert */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-card" data-id="element-508">
              <div className="flex items-start justify-between mb-3" data-id="element-509">
                <div className="flex items-center gap-2 text-xs font-medium text-navy-600 bg-slate-100 px-2 py-1 rounded-md" data-id="element-510">
                  <Search size={12} data-id="element-511" />
                  Palabra Clave
                </div>
                <button className="text-slate-400 hover:text-navy-950" data-id="element-512">
                  <MoreVertical size={16} data-id="element-513" />
                </button>
              </div>
              <h3 className="text-base font-medium text-navy-950 mb-1" data-id="element-514">
                "Vademécum PAMI"
              </h3>
              <p className="text-sm text-navy-600 mb-4" data-id="element-515">
                Búsqueda exacta en Boletín Oficial y resoluciones del Ministerio
                de Salud.
              </p>
              <div className="flex items-center justify-between pt-4 border-t border-slate-100" data-id="element-516">
                <div className="flex flex-col" data-id="element-517">
                  <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold" data-id="element-518">
                    Matches Totales
                  </span>
                  <span className="text-sm font-medium text-navy-950" data-id="element-519">
                    4 documentos
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-520">
                  <span className="flex h-2 w-2 relative" data-id="element-521">
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" data-id="element-522"></span>
                  </span>
                  <span className="text-xs font-medium text-emerald-600" data-id="element-523">
                    Activa
                  </span>
                </div>
              </div>
            </div>

            {/* Another keyword alert */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-card" data-id="element-524">
              <div className="flex items-start justify-between mb-3" data-id="element-525">
                <div className="flex items-center gap-2 text-xs font-medium text-navy-600 bg-slate-100 px-2 py-1 rounded-md" data-id="element-526">
                  <Search size={12} data-id="element-527" />
                  Palabra Clave
                </div>
                <button className="text-slate-400 hover:text-navy-950" data-id="element-528">
                  <MoreVertical size={16} data-id="element-529" />
                </button>
              </div>
              <h3 className="text-base font-medium text-navy-950 mb-1" data-id="element-530">
                "Licitación pública salud"
              </h3>
              <p className="text-sm text-navy-600 mb-4" data-id="element-531">
                Monitoreo en Comprar.gob.ar y Boletín Oficial.
              </p>
              <div className="flex items-center justify-between pt-4 border-t border-slate-100" data-id="element-532">
                <div className="flex flex-col" data-id="element-533">
                  <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold" data-id="element-534">
                    Matches Totales
                  </span>
                  <span className="text-sm font-medium text-navy-950" data-id="element-535">
                    23 documentos
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-536">
                  <span className="flex h-2 w-2 relative" data-id="element-537">
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" data-id="element-538"></span>
                  </span>
                  <span className="text-xs font-medium text-emerald-600" data-id="element-539">
                    Activa
                  </span>
                </div>
              </div>
            </div>

            {/* Semantic alert 2 */}
            <div className="bg-white rounded-xl border border-brand-200 p-5 shadow-card relative overflow-hidden group" data-id="element-540">
              <div className="absolute top-0 right-0 w-24 h-24 bg-brand-50 rounded-bl-full -z-10 group-hover:scale-110 transition-transform" data-id="element-541" />
              <div className="flex items-start justify-between mb-3" data-id="element-542">
                <div className="flex items-center gap-2 text-xs font-medium text-brand-600 bg-brand-50 px-2 py-1 rounded-md border border-brand-100" data-id="element-543">
                  <Sparkles size={12} data-id="element-544" />
                  Alerta Semántica Premium
                </div>
                <button className="text-slate-400 hover:text-navy-950" data-id="element-545">
                  <MoreVertical size={16} data-id="element-546" />
                </button>
              </div>
              <h3 className="text-base font-medium text-navy-950 mb-1" data-id="element-547">
                Importación principios activos
              </h3>
              <p className="text-sm text-navy-600 mb-4 line-clamp-2" data-id="element-548">
                Alertar sobre nuevos requisitos, restricciones o cambios en
                procesos de habilitación para importación de principios activos
                farmacéuticos.
              </p>
              <div className="flex items-center justify-between pt-4 border-t border-slate-100" data-id="element-549">
                <div className="flex flex-col" data-id="element-550">
                  <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold" data-id="element-551">
                    Matches Totales
                  </span>
                  <span className="text-sm font-medium text-navy-950" data-id="element-552">
                    7 documentos
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-553">
                  <span className="flex h-2 w-2 relative" data-id="element-554">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" data-id="element-555"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" data-id="element-556"></span>
                  </span>
                  <span className="text-xs font-medium text-emerald-600" data-id="element-557">
                    Activa
                  </span>
                </div>
              </div>
            </div>
          </div> : <div className="bg-white rounded-xl border border-slate-200 shadow-card overflow-hidden" data-id="element-558">
            <div className="overflow-x-auto" data-id="element-559">
              <table className="w-full text-left border-collapse" data-id="element-560">
                <thead data-id="element-561">
                  <tr className="border-b border-slate-200 bg-slate-50/50" data-id="element-562">
                    <th className="py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-563">
                      Fecha
                    </th>
                    <th className="py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-564">
                      Documento
                    </th>
                    <th className="py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-565">
                      Alerta Origen
                    </th>
                    <th className="py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-566">
                      Estado
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-id="element-567">
                  {[{
                id: 1,
                date: '02 Abr 2026',
                doc: 'Disposición 2341/2026 - ANMAT',
                alert: 'Cambios en trazabilidad',
                type: 'semantic',
                status: 'sinLeer'
              }, {
                id: 2,
                date: '01 Abr 2026',
                doc: 'Resolución 876/2026 - SENASA',
                alert: 'Importación principios activos',
                type: 'semantic',
                status: 'leido'
              }, {
                id: 3,
                date: '28 Mar 2026',
                doc: 'Boletín Oficial Sección 1 - Pág. 34',
                alert: '"Vademécum PAMI"',
                type: 'keyword',
                status: 'leido'
              }, {
                id: 4,
                date: '25 Mar 2026',
                doc: 'Disposición 2298/2026 - ANMAT',
                alert: 'Cambios en trazabilidad',
                type: 'semantic',
                status: 'leido'
              }, {
                id: 5,
                date: '22 Mar 2026',
                doc: 'Licitación Pública 45/2026 - Min. Salud',
                alert: '"Licitación pública salud"',
                type: 'keyword',
                status: 'leido'
              }, {
                id: 6,
                date: '20 Mar 2026',
                doc: 'Resolución 812/2026 - ANMAT',
                alert: 'Importación principios activos',
                type: 'semantic',
                status: 'leido'
              }].map(item => <tr key={item.id} className="hover:bg-slate-50 transition-colors cursor-pointer" data-id="element-568">
                      <td className="py-3 px-4 text-sm text-navy-600 whitespace-nowrap" data-id="element-569">
                        {item.date}
                      </td>
                      <td className="py-3 px-4" data-id="element-570">
                        <div className="flex items-center gap-2" data-id="element-571">
                          <FileText size={14} className="text-slate-400 shrink-0" data-id="element-572" />
                          <span className="text-sm font-medium text-navy-950" data-id="element-573">
                            {item.doc}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4" data-id="element-574">
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 bg-brand-50 px-2 py-1 rounded-md" data-id="element-575">
                          {item.type === 'semantic' ? <Sparkles size={10} data-id="element-576" /> : <Search size={10} data-id="element-577" />}
                          {item.alert}
                        </span>
                      </td>
                      <td className="py-3 px-4" data-id="element-578">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${item.status === 'sinLeer' ? 'bg-rose-50 text-rose-600' : 'bg-emerald-50 text-emerald-600'}`} data-id="element-579">
                          {item.status === 'sinLeer' ? 'Sin Leer' : 'Leído'}
                        </span>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>}
      </div>

      {/* Create Alert Modal */}
      <AnimatePresence data-id="element-580">
        {showCreateModal && <CreateAlertModal onClose={() => setShowCreateModal(false)} data-id="element-581" />}
      </AnimatePresence>
    </AppShell>;
}